import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { DeviceId, DevicePropertyValue } from './models/Device';
import { Device, DeviceProperty, EditableSettingsProperty, PropertyId, ResolvedSection } from './models/DeviceDetail';
import { DeviceDetailOverviewVm } from './models/DeviceDetailOverviewVm';
import { DeviceStateUpdateExecutionInfo, ExecutionInfo } from './models/Execution';
import { UiProfile, UiProfileProperty } from './models/UiProfile';
import { EditedProperty } from './models/UpdateDeviceCommand';
import { DEVICE_GATEWAY } from './services/DeviceGateway';
import { SiteDevicesStore } from './site-devices.store';
import type {
    DeviceContext,
    DeviceDiagnosticSectionsContext,
    DeviceSettingsSectionContext,
} from './utils/build-device-sections';
import {
    buildDeviceOverviewVm,
    buildEditableSettings,
    buildPropertyEditor,
    diagnosticSections,
    resolveSections,
    settingsSections,
} from './utils/device-detail';
import { SectionItemEditor } from './utils/editor-builder/buildEditor';
import { enrichUiProperty } from './utils/enrich-ui-property';

interface LoadInProgress {
    ticket: number;
    /** The states received since this load started: its response may predate them. */
    states: Array<{ stateId: PropertyId; value: DevicePropertyValue }>;
}

interface DeviceDetailEntry {
    device: Device;
    uiProfile: UiProfile;
}

/**
 * The detail of the devices that were opened: their properties and their UI profile, kept up to date by the real-time
 * state changes.
 * - `load` fetches a device again each time it is asked, and the known detail stays readable meanwhile. A response is
 *   dropped when a newer load of the same device was already applied, so an old response never replaces a newer state.
 * - A state change received while a device loads is applied again on top of the response, which may predate it.
 * - A state change for a device that was never loaded, and is not loading, is ignored: the load returns the fresh device.
 * - The state lives as long as the page: logging out leaves the application, so there is no session reset.
 */
@Injectable({ providedIn: 'root' })
export class DeviceDetailStore {
    private readonly deviceGateway = inject(DEVICE_GATEWAY);
    private readonly siteDevices = inject(SiteDevicesStore);

    private readonly entries = signal<Record<DeviceId, DeviceDetailEntry>>({});
    // An entry is replaced, never mutated, so its sections stay valid for as long as the entry lives.
    private readonly resolvedSections = new WeakMap<DeviceDetailEntry, ResolvedSection[]>();
    private readonly loading = new Map<DeviceId, Set<LoadInProgress>>();
    // The ticket of the load whose response each entry holds. Tickets only move forward.
    private readonly applied = new Map<DeviceId, number>();
    private lastTicket = 0;

    /** Rejects when the device cannot be fetched. A UI profile that cannot be fetched is an empty one. */
    async load(deviceId: DeviceId): Promise<Device> {
        const load: LoadInProgress = { ticket: ++this.lastTicket, states: [] };
        const loads = this.loading.get(deviceId) ?? new Set<LoadInProgress>();
        loads.add(load);
        this.loading.set(deviceId, loads);

        try {
            const device = await firstValueFrom(this.deviceGateway.fetchDevice(deviceId));
            if (!device) throw new Error(`Device ${deviceId} not found`);

            const uiProfile = await firstValueFrom(this.deviceGateway.getDeviceUI(device.profileId));
            if (load.ticket < (this.applied.get(deviceId) ?? 0)) return device;

            this.applied.set(deviceId, load.ticket);
            this.entries.update((entries) => ({ ...entries, [device.id]: { device, uiProfile } }));
            load.states.forEach(({ stateId, value }) => this.applyState(deviceId, stateId, value));
            if (device.rawData) this.siteDevices.deviceUpdated(device.rawData.site_id, device.rawData);

            return device;
        } finally {
            loads.delete(load);
            if (loads.size === 0) this.loading.delete(deviceId);
        }
    }

    stateUpdated(deviceId: DeviceId, stateId: PropertyId, value: DevicePropertyValue): void {
        this.loading.get(deviceId)?.forEach((load) => load.states.push({ stateId, value }));
        this.applyState(deviceId, stateId, value);
    }

    device(deviceId: DeviceId | null | undefined): Device | null {
        return this.entry(deviceId)?.device ?? null;
    }

    uiProfile(deviceId: DeviceId | null | undefined): UiProfile | null {
        return this.entry(deviceId)?.uiProfile ?? null;
    }

    properties(deviceId: DeviceId | null | undefined): Record<PropertyId, DeviceProperty> | undefined {
        return this.entry(deviceId)?.device.properties;
    }

    property(deviceId: DeviceId, propertyId: PropertyId): DeviceProperty | undefined {
        return this.properties(deviceId)?.[propertyId];
    }

    /** The device with its UI profile, or null while either is unknown. */
    context(deviceId: DeviceId | null | undefined): DeviceContext | null {
        const entry = this.entry(deviceId);
        return entry && deviceId ? { deviceId, device: entry.device, uiProfile: entry.uiProfile } : null;
    }

    /** `sectionRefreshTasks` are the section refreshes under way, tracked by `DeviceCommandsStore`. */
    diagnosticContext(
        deviceId: DeviceId | null | undefined,
        sectionRefreshTasks: ExecutionInfo[] | undefined,
    ): DeviceDiagnosticSectionsContext {
        return {
            ctx: this.context(deviceId),
            uiProfile: this.uiProfile(deviceId),
            properties: this.properties(deviceId),
            sections: this.sections(deviceId),
            sectionRefreshTasks,
        };
    }

    /** The intervention is tracked outside this store, and the executions by `DeviceCommandsStore`. */
    settingsContext(
        deviceId: DeviceId | null | undefined,
        tracked: {
            isInIntervention: boolean;
            executionInfos: DeviceStateUpdateExecutionInfo[] | undefined;
            sectionRefreshTasks: ExecutionInfo[] | undefined;
        },
    ): DeviceSettingsSectionContext {
        return {
            ctx: this.context(deviceId),
            editableProperties: this.editableSettings(deviceId),
            settingsSections: this.settingsSections(deviceId),
            ...tracked,
        };
    }

    sections(deviceId: DeviceId | null | undefined): ResolvedSection[] | undefined {
        const entry = this.entry(deviceId);
        return entry && this.sectionsOf(entry);
    }

    diagnosticSections(deviceId: DeviceId | null | undefined): ResolvedSection[] | undefined {
        const sections = this.sections(deviceId);
        return sections && diagnosticSections(sections);
    }

    settingsSections(deviceId: DeviceId | null | undefined): ResolvedSection[] | undefined {
        const sections = this.sections(deviceId);
        return sections && settingsSections(sections);
    }

    settingsPropertyIds(deviceId: DeviceId | null | undefined): string[] {
        return this.settingsSections(deviceId)?.flatMap((section) => section.propertyIds) ?? [];
    }

    overview(deviceId: DeviceId | null | undefined): DeviceDetailOverviewVm | null {
        const entry = this.entry(deviceId);
        return entry ? buildDeviceOverviewVm(entry.device, entry.uiProfile, this.sectionsOf(entry)) : null;
    }

    editableSettings(deviceId: DeviceId | null | undefined): EditableSettingsProperty[] | undefined {
        const entry = this.entry(deviceId);
        return entry && buildEditableSettings(entry.device, entry.uiProfile, this.sectionsOf(entry));
    }

    uiProperty(deviceId: DeviceId, propertyId: PropertyId): UiProfileProperty | undefined {
        return enrichUiProperty(
            this.uiProfile(deviceId)?.properties?.[propertyId],
            this.property(deviceId, propertyId)?.metadata,
        );
    }

    propertyInfos(deviceId: DeviceId, propertyId: PropertyId): EditedProperty {
        const uiProperty = this.uiProperty(deviceId, propertyId);
        return { id: propertyId, label: uiProperty?.label ?? propertyId, writeType: uiProperty?.writeType ?? '' };
    }

    editor(deviceId: DeviceId, propertyId: PropertyId): SectionItemEditor | undefined {
        const property = this.property(deviceId, propertyId);
        return property && buildPropertyEditor(this.uiProperty(deviceId, propertyId), property);
    }

    private applyState(deviceId: DeviceId, stateId: PropertyId, value: DevicePropertyValue): void {
        this.entries.update((entries) => {
            const entry = entries[deviceId];
            if (!entry) return entries;

            const { device } = entry;
            const properties = {
                ...device.properties,
                [stateId]: { ...device.properties[stateId], id: stateId, value },
            };
            const available = stateId === 'available' ? (value as boolean) : device.available;

            return { ...entries, [deviceId]: { ...entry, device: { ...device, properties, available } } };
        });
    }

    private sectionsOf(entry: DeviceDetailEntry): ResolvedSection[] {
        let sections = this.resolvedSections.get(entry);
        if (!sections) {
            sections = resolveSections(entry.device, entry.uiProfile);
            this.resolvedSections.set(entry, sections);
        }
        return sections;
    }

    private entry(deviceId: DeviceId | null | undefined): DeviceDetailEntry | undefined {
        return deviceId ? this.entries()[deviceId] : undefined;
    }
}
